import { jsPDF } from 'jspdf';

const ACCENT = '#7c6fff';
const INK = '#14151c';
const GRAY = '#6b6e85';
const LIGHT_GRAY = '#e2e2ec';

function safeFilename(title) {
  return (title || 'conversation')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'conversation';
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Downloads a conversation as a JSON file — a full, lossless backup of every
 * message (role, content, image) plus title and timestamps.
 */
export function downloadConversationJson(conversation) {
  const payload = {
    id: conversation.id,
    title: conversation.title,
    updatedAt: new Date(conversation.updatedAt).toISOString(),
    messages: conversation.messages.map((m) => ({
      role: m.role,
      content: m.content,
      ...(m.image ? { image: m.image } : {}),
    })),
  };

  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  triggerDownload(blob, `piua-${safeFilename(conversation.title)}.json`);
}

/**
 * Downloads a conversation as a readable PDF transcript. Entirely
 * client-side, mirroring the invoice PDF export pattern.
 */
export function downloadConversationPdf(conversation) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 48;
  const contentRight = pageWidth - margin;
  const contentWidth = contentRight - margin;
  const footerLimit = pageHeight - 56;

  let y = 0;

  function ensureSpace(height) {
    if (y + height > footerLimit) {
      doc.addPage();
      y = 56;
    }
  }

  function drawHeader() {
    doc.setFillColor(ACCENT);
    doc.roundedRect(margin, 40, 28, 28, 6, 6, 'F');
    doc.setTextColor('#ffffff');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('P', margin + 14, 59, { align: 'center' });

    doc.setTextColor(INK);
    doc.setFontSize(15);
    doc.text('Piua AI', margin + 38, 59);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(GRAY);
    doc.text(new Date(conversation.updatedAt).toLocaleString(), contentRight, 59, { align: 'right' });

    doc.setDrawColor(LIGHT_GRAY);
    doc.line(margin, 82, contentRight, 82);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(INK);
    doc.text(conversation.title || 'Conversation', margin, 110);

    y = 136;
  }

  drawHeader();

  const roleLabels = { user: 'You', assistant: 'Piua AI' };

  for (const message of conversation.messages) {
    const label = roleLabels[message.role] || message.role;
    ensureSpace(30);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(message.role === 'user' ? ACCENT : GRAY);
    doc.text(label, margin, y);
    y += 16;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10.5);
    doc.setTextColor(INK);
    const lines = doc.splitTextToSize(message.content || '(empty)', contentWidth);
    for (const line of lines) {
      ensureSpace(15);
      doc.text(line, margin, y);
      y += 15;
    }

    if (message.image) {
      ensureSpace(15);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9.5);
      doc.setTextColor(GRAY);
      doc.text('[Generated image attached]', margin, y);
      y += 15;
    }

    y += 14;
  }

  doc.save(`piua-${safeFilename(conversation.title)}.pdf`);
}
